import dayjs, { type Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

export function formatDate(date: Date | number | string, template: string): string {
  return utcDate(date).format(template);
}

/** Format a UTC instant in a specific IANA timezone (e.g. the viewer's local zone). */
export function formatDateInTz(date: Date | number | string, template: string, tz: string): string {
  return utcDate(date).tz(tz).format(template);
}

export const utcDate = dayjs.utc;

/**
 * The calendar day something falls on, in the reader's zone — `'YYYY-MM-DD'`.
 *
 * One definition, because a day key is a rule, not a format string: the feed
 * groups events by it, buckets session tickets by it, bounds its window with
 * it, and compares the three with `<`. Two spellings of "which day is this" is
 * how the daily-session cap once drifted, and the same trap sits here — an
 * event and the session it is about have to land on the same card.
 */
export const DAY_KEY_FORMAT = 'YYYY-MM-DD';

/** The day key of an instant, converted into `timezone` first. */
export const dayKey = (date: Date | number | string, timezone: string): string =>
  utcDate(date).tz(timezone).format(DAY_KEY_FORMAT);

/** The day key of a moment already in the reader's zone — for callers doing
 *  calendar arithmetic (`today.add(1, 'day')`), which must stay on dayjs to
 *  survive DST rather than adding 86_400_000. */
export const dayKeyOf = (date: Dayjs): string => date.format(DAY_KEY_FORMAT);

/**
 * Reschedule is free until 24h before the class starts — the single UI-side
 * definition of that window (the backend enforces it independently). Every
 * surface deciding Join-vs-Reschedule or gating a reschedule reads this.
 */
export function canRescheduleClass(start: Date | number | string): boolean {
  return dayjs.utc().isBefore(dayjs.utc(start).subtract(24, 'hour'));
}

/**
 * Whether a call is behind the reader — the single definition of it, and the end
 * rather than the start, because a call in progress is not over.
 *
 * The clock is a parameter because the two callers hold different ones and both
 * are right: the page splits its list against the server's `now` at render, and
 * a call card re-reads its own end off a ticking clock, since a call ends while
 * the page stands open. One rule, two clocks — a second spelling of it is how a
 * grace period would land on one surface and not the other.
 */
export const hasEnded = (end: Date | number | string, now: number = Date.now()): boolean =>
  !utcDate(now).isBefore(utcDate(end));

/** "2 days ago" / "in 5 days" — for lists where the exact day is one click away
 *  and the distance is what the reader is actually judging. */
export const fromNow = (date: Date | number | string): string => utcDate(date).fromNow();

export const getUtcOffset = (timezone: string, date: Date | string | number) =>
  utcDate(date).tz(timezone).utcOffset();

export const guessTimezone = () => {
  // Only detect timezone in browser, not during SSR
  if (typeof window === 'undefined') {
    return 'UTC';
  }
  return dayjs.tz.guess();
};
